import React from "react";
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from "react-router-dom";
import { Tooltip } from '@mui/material';
import {
  Dashboard as LayoutDashboard,
  Inventory as Package,
  LocalOffer as Ticket,
  BarChart as BarChart3,
  People as Users,
  Category as Grid3X3,
  Map,
  ShoppingCart,
  LocalFireDepartment as FireIcon,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';

const EXPANDED_WIDTH = 280;
const COLLAPSED_WIDTH = 64;

const SidebarContainer = styled('aside')`
  position: fixed;
  left: 0;
  top: 80px;
  width: ${props => props.collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH}px;
  height: calc(100vh - 80px);
  background: var(--surface);
  border-right: 1px solid var(--border);
  z-index: var(--z-sidebar);
  overflow-y: auto;
  overflow-x: hidden;
  transition: width var(--transition-normal);

  @media (max-width: 768px) {
    transform: translateX(-100%);
  }
`;

const SidebarHeader = styled('div')`
  padding: var(--space-lg);
  border-bottom: 1px solid var(--border-subtle);
  display: flex;
  align-items: center;
  justify-content: ${props => props.collapsed ? 'center' : 'space-between'};
  min-height: 64px;
  gap: var(--space-sm);
`;

const SidebarTitle = styled('h3')`
  color: var(--text-secondary);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  flex: 1;
`;

const ToggleButton = styled('button')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: var(--surface-hover);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-normal);

  &:hover {
    background: var(--accent-primary);
    color: #ffffff;
    border-color: var(--accent-primary);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const MenuList = styled('ul')`
  list-style: none;
  padding: var(--space-md) 0;
  margin: 0;
`;

const MenuItem = styled('li')`
  margin: 0 ${props => props.collapsed ? '8px' : 'var(--space-md)'} var(--space-xs);
  transition: margin var(--transition-normal);
`;

const MenuLink = styled('button')`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: ${props => props.collapsed ? 'center' : 'flex-start'};
  padding: ${props => props.collapsed ? 'var(--space-md)' : 'var(--space-md) var(--space-lg)'};
  border: none;
  border-radius: var(--radius-md);
  background: ${props => props.active ? 'var(--accent-primary)' : 'transparent'};
  color: ${props => props.active ? '#ffffff' : 'var(--text-secondary)'};
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: all var(--transition-normal);
  position: relative;
  overflow: hidden;

  &:hover {
    background: ${props => props.active ? 'var(--accent-hover)' : 'var(--surface-hover)'};
    color: ${props => props.active ? '#ffffff' : 'var(--text-primary)'};
    transform: ${props => props.collapsed ? 'none' : 'translateX(4px)'};
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: var(--accent-primary);
    transform: scaleY(${props => props.active ? '1' : '0'});
    transition: transform var(--transition-normal);
  }

  &:hover::before {
    transform: scaleY(1);
  }
`;

const MenuIcon = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-right: ${props => props.collapsed ? '0' : 'var(--space-md)'};
  transition: margin var(--transition-normal);

  svg {
    width: 18px;
    height: 18px;
  }
`;

const MenuText = styled('span')`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
`;

function Sidebar({ collapsed, onToggle }) {
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    { text: "Dashboard",  icon: <LayoutDashboard />, path: "/dashboard",  description: "Overview & analytics"          },
    { text: "Products",   icon: <Package />,          path: "/products",   description: "Manage inventory"              },
    { text: "Promotions", icon: <FireIcon />,          path: "/promotions", description: "Deal & price drop management"  },
    { text: "Vouchers",   icon: <Ticket />,            path: "/vouchers",   description: "Discount management"           },
    { text: "Reports",    icon: <BarChart3 />,          path: "/reports",    description: "Analytics & insights"          },
    { text: "Users",      icon: <Users />,             path: "/users",      description: "User management"               },
    { text: "Categories", icon: <Grid3X3 />,           path: "/categories", description: "Product categories"            },
    { text: "Map",        icon: <Map />,               path: "/map",        description: "Store locations"               },
    { text: "Trolleys",   icon: <ShoppingCart />,      path: "/trolleys",   description: "Cart management"               },
  ];

  return (
    <SidebarContainer collapsed={collapsed}>
      <SidebarHeader collapsed={collapsed}>
        {!collapsed && <SidebarTitle>Navigation</SidebarTitle>}
        <Tooltip title={collapsed ? "Expand sidebar" : "Collapse sidebar"} placement="right">
          <ToggleButton onClick={onToggle}>
            {collapsed ? <ChevronRight /> : <ChevronLeft />}
          </ToggleButton>
        </Tooltip>
      </SidebarHeader>

      <nav>
        <MenuList>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;

            const linkEl = (
              <MenuItem key={item.text} collapsed={collapsed}>
                <MenuLink
                  active={isActive}
                  collapsed={collapsed}
                  onClick={() => navigate(item.path)}
                  title={!collapsed ? item.description : undefined}
                >
                  <MenuIcon collapsed={collapsed}>{item.icon}</MenuIcon>
                  {!collapsed && <MenuText>{item.text}</MenuText>}
                </MenuLink>
              </MenuItem>
            );

            return collapsed ? (
              <Tooltip key={item.text} title={item.text} placement="right">
                {linkEl}
              </Tooltip>
            ) : linkEl;
          })}
        </MenuList>
      </nav>
    </SidebarContainer>
  );
}

export default Sidebar;
